import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAccountDto, IdContactDto } from '@/services/ws-crm/src/main/resources/rest-api';
import {
  ContactRole,
  getAccountShortById,
  getContactById,
  getContactsByCompanyAndRole,
} from '@/services/ws-crm/src/main/resources/rest-api';
import type { IdSalesPromotionDto, IdTaskTopicDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import {
  getAccountList,
  getSalesContacts,
  getSalesPromotionById,
  getSalesPromotions,
  getSource,
  getSources,
  getTaskTopics,
  getTaskTopicsByIds,
  OutcomeFilter,
  SalesPromotionStatus,
} from '@/services/ws-funnel/src/main/resources/rest-api';
import type { IdLabelDto } from '@/services/ws-labels/src/main/resources/rest-api';
import { getLabelById, getLabels, LabelType } from '@/services/ws-labels/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useIdentityUser } from '@/js/libs/use-auth';
import { useLazyRequest, useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT, DEFAULT_PAGE } from 'src/constants';
import { createSingleNullableEnumOptionFilterConfig, createStringFilterConfig, useFilters } from 'src/hooks/useFilters';
import { StorageScope } from 'src/hooks/useSessionStorage';
import { ErrorCode } from 'src/types';
import { useTableStatus } from './hooks';
import type { FiltersState } from './types';
import { TaskTableStatus, TimeFrame } from './types';

export const { useTaskTablesContext } = createContexts({
  TaskTables() {
    const [t] = useTranslation(['backend', 'common']);
    const user = useIdentityUser();
    const { status, updateStatus } = useTableStatus();

    const getTopicsRequest = useLazyRequest({
      apiMethod: getTaskTopicsByIds,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getLabelRequest = useLazyRequest({
      apiMethod: getLabelById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getSourceRequest = useLazyRequest({
      apiMethod: getSource,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getContactRequest = useLazyRequest({
      apiMethod: getContactById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getAccountRequest = useLazyRequest({
      apiMethod: getAccountShortById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getPromotionRequest = useLazyRequest({
      apiMethod: getSalesPromotionById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const {
      filters$,
      multiFilter$,
      updateFilter,
      isFilterHidden,
      areFiltersPreloading$,
      areSearchParamFiltersEmpty,
      areStorageParamFiltersEmpty,
      MultiFilter,
      ClearFiltersButton,
    } = useFilters<FiltersState>({
      storageScope: StorageScope.SALES_TASK_TABLE,
      filterConfigs: {
        search: createStringFilterConfig(),
        topic: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:TOPIC'),
          compare: (a, b) => a?.value === b?.value,
          serialize: (option) => option?.value.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getTopicsRequest.load({ ids: [stored as unknown as IdTaskTopicDto] });
            const topic = data.items[0];
            if (topic?.id) {
              return { label: topic.name, value: topic.id };
            }
            return null;
          },
        },
        outcome: createSingleNullableEnumOptionFilterConfig(OutcomeFilter, {
          getLabel: () => t('common:OUTCOME'),
          hidden: status !== TaskTableStatus.DONE,
        }),
        timeFrame: createSingleNullableEnumOptionFilterConfig(TimeFrame, {
          getLabel: () => t('common:TIME_FRAME'),
          hidden: status !== TaskTableStatus.DONE,
        }),
        label: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:LABEL'),
          compare: (a, b) => a?.value === b?.value,
          serialize: (option) => option?.value.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getLabelRequest.load({ id: stored as unknown as IdLabelDto });
            return { label: data.name, value: data.id };
          },
        },
        source: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:SOURCE'),
          compare: (a, b) => a?.value === b?.value,
          serialize: (option) => option?.value ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getSourceRequest.load({ id: stored });
            if (data.id) {
              return { label: data.name ?? data.id, value: data.id };
            }
            return null;
          },
        },
        sales: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:SALES'),
          compare: (a, b) => a?.value.id === b?.value.id,
          hidden: status !== TaskTableStatus.UNASSIGNED,
          serialize: (option) => option?.value.id.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getContactRequest.load({ id: stored as unknown as IdContactDto });
            return { label: data.name, value: data };
          },
        },
        assignedContact: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:ASSIGNED_CONTACT'),
          compare: (a, b) => a?.value === b?.value,
          hidden: status === TaskTableStatus.UNASSIGNED,
          serialize: (option) => option?.value.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getContactRequest.load({ id: stored as unknown as IdContactDto });
            return { label: data.name, value: data.id };
          },
        },
        invoicingAccount: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:INVOICING_ACCOUNT'),
          compare: (a, b) => a?.value === b?.value,
          serialize: (option) => option?.value.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getAccountRequest.load({ accountId: stored as unknown as IdAccountDto });
            return { label: data.name, value: data.id };
          },
        },
        promotion: {
          defaultValue: null,
          emptyValue: null,
          getLabel: () => t('common:PROMOTION'),
          compare: (a, b) => a?.value === b?.value,
          serialize: (option) => option?.value.toString() ?? null,
          asyncDeserialize: async (stored) => {
            const { data } = await getPromotionRequest.load({ salesPromotionId: stored as unknown as IdSalesPromotionDto });
            return { label: data.name, value: data.id };
          },
        },
      },
    });

    const [topicPrefix, setTopicPrefix] = useState<string>('');
    const [labelPrefix, setLabelPrefix] = useState<string>('');
    const [sourcePrefix, setSourcePrefix] = useState<string>('');
    const [salesPrefix, setSalesPrefix] = useState<string>('');
    const [assignedContactPrefix, setAssignedContactPrefix] = useState<string>('');
    const [invoicingAccountPrefix, setInvoicingAccountPrefix] = useState<string>('');
    const [promotionPrefix, setPromotionPrefix] = useState<string>('');

    const topicRequest = useRequest({
      apiMethod: getTaskTopics,
      params: { nameFilter: topicPrefix || undefined, page: DEFAULT_PAGE, limit: DROP_DOWN_ENTITY_LIMIT, sort: 'name' },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    const labelRequest = useRequest({
      apiMethod: getLabels,
      params: {
        nameFilter: labelPrefix || undefined,
        type: LabelType.SALESFUNNEL,
        page: DEFAULT_PAGE,
        limit: DROP_DOWN_ENTITY_LIMIT,
        sort: 'name',
      },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    const sourceRequest = useRequest({
      apiMethod: getSources,
      params: { nameFilter: sourcePrefix || undefined, page: DEFAULT_PAGE, limit: DROP_DOWN_ENTITY_LIMIT, sort: 'name' },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    const salesRequest = useRequest({
      apiMethod: getContactsByCompanyAndRole,
      params: {
        filter: salesPrefix || undefined,
        role: ContactRole.SALES,
        companyId: user?.company.id,
        page: DEFAULT_PAGE,
        limit: DROP_DOWN_ENTITY_LIMIT,
      },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    const assignedContactRequest = useRequest({
      apiMethod: getSalesContacts,
      params: { filter: assignedContactPrefix, includeInactive: false, page: DEFAULT_PAGE, limit: DROP_DOWN_ENTITY_LIMIT },
      debounce: DEFAULT_DEBOUNCE_DELAY,
      onSuccess: ({ data }) => {
        if (areSearchParamFiltersEmpty && areStorageParamFiltersEmpty) {
          const assignedContact = data.items.find((item) => item.email === user?.email);
          if (assignedContact) {
            updateFilter('assignedContact', { label: assignedContact.name, value: assignedContact.id });
          }
        }
      },
    });

    const invoicingAccountRequest = useRequest({
      apiMethod: getAccountList,
      params: {
        prefix: invoicingAccountPrefix || undefined,
        includeInactive: true,
        page: DEFAULT_PAGE,
        limit: DROP_DOWN_ENTITY_LIMIT,
        sort: 'name',
      },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    const promotionRequest = useRequest({
      apiMethod: getSalesPromotions,
      params: {
        filter: {
          searchFilter: promotionPrefix || undefined,
          status: SalesPromotionStatus.ACTIVE,
          deadlineNotReached: true,
          page: DEFAULT_PAGE,
          limit: DROP_DOWN_ENTITY_LIMIT,
          sort: 'name',
        },
      },
      debounce: DEFAULT_DEBOUNCE_DELAY,
    });

    return {
      status,
      updateStatus,
      filters$,
      multiFilter$,
      updateFilter,
      isFilterHidden,
      areFiltersPreloading$,
      MultiFilter,
      ClearFiltersButton,

      topicPrefix,
      setTopicPrefix,
      labelPrefix,
      setLabelPrefix,
      sourcePrefix,
      setSourcePrefix,
      salesPrefix,
      setSalesPrefix,
      assignedContactPrefix,
      setAssignedContactPrefix,
      invoicingAccountPrefix,
      setInvoicingAccountPrefix,
      promotionPrefix,
      setPromotionPrefix,

      assignedContactRequest,
      invoicingAccountRequest,
      labelRequest,
      promotionRequest,
      salesRequest,
      sourceRequest,
      topicRequest,
    };
  },
});
